import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nosible/shared/components/ui/table";
import { Badge } from "@nosible/shared/components/ui/badge";
import { Button } from "@nosible/shared/components/ui/button";
import { Input } from "@nosible/shared/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@nosible/shared/components/ui/alert-dialog";
import { trpc } from "@/admin/lib/trpc";
import {
  Ban,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";

export function AdminUsers() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  const deleteMode = mode === "delete";
  const updateMode = mode === "update";
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState("");
  const [userToDelete, setUserToDelete] = useState<{
    id: string;
    name: string;
    email: string;
  } | null>(null);
  const pageSize = 10;
  const utils = trpc.useUtils();

  const {
    data: usersData,
    isLoading,
    error,
  } = trpc.admin.listUsers.useQuery(
    {
      limit: pageSize,
      offset: page * pageSize,
      searchValue: query || undefined,
    },
    {
      enabled: true,
    }
  );

  const deleteMutation = trpc.admin.deleteUser.useMutation({
    onSuccess: async () => {
      setUserToDelete(null);
      await utils.admin.listUsers.invalidate();
    },
  });

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-silica">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-red-400">Error loading users: {error.message}</div>
      </div>
    );
  }

  const totalPages = Math.ceil((usersData?.total ?? 0) / pageSize);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Users className="h-6 w-6 text-violet-pulse" />
          <h1 className="text-2xl font-bold text-silica font-display">
            {deleteMode
              ? "Delete a user"
              : updateMode
                ? "Update a user"
                : "Users"}
          </h1>
        </div>
        {!deleteMode && !updateMode && (
          <Button className="brand-primary-button" size="sm" asChild>
            <Link to="/users/create">
              <UserPlus className="h-4 w-4 mr-2" />
              Create User
            </Link>
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {deleteMode
              ? "Select a user to delete"
              : updateMode
                ? "Select a user to update"
                : "All Users"}
          </CardTitle>
          <CardDescription>
            {deleteMode
              ? "Deleting an account removes its owned subscriptions and credentials while preserving shared organization access for other users."
              : updateMode
                ? "Choose a user account to edit its name, email address, or role."
              : "Create, inspect, update, or safely delete user accounts."}{" "}
            {query ? "Matching" : "Total"}: {usersData?.total ?? 0} users
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4 max-w-md">
            <label className="text-sm font-medium text-silica" htmlFor="admin-user-search">Search users</label>
            <Input
              id="admin-user-search"
              value={query}
              onChange={(event) => { setQuery(event.target.value); setPage(0); }}
              placeholder="Name, email, or identifier"
            />
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>
                  {deleteMode ? "Delete" : updateMode ? "Update" : "Actions"}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usersData?.users.map((user: NonNullable<typeof usersData>["users"][number]) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium text-silica">
                    {user.name}
                  </TableCell>
                  <TableCell className="text-chromium-mist">
                    {user.email}
                    {user.emailVerified && (
                      <Badge variant="outline" className="ml-2 text-neon-green border-neon-green">
                        Verified
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={user.role === "admin" ? "default" : "secondary"}
                      className={user.role === "admin" ? "bg-violet-pulse" : ""}
                    >
                      {user.role || "user"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.banned ? (
                      <Badge variant="destructive" className="flex items-center gap-1 w-fit">
                        <Ban className="h-3 w-3" />
                        Banned
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-neon-green border-neon-green">
                        Active
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-chromium-mist">
                    {user.createdAt
                      ? new Date(user.createdAt).toLocaleDateString()
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <div className="admin-card-actions">
                      {!deleteMode && !updateMode && (
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/users/${user.id}`}>View</Link>
                        </Button>
                      )}
                      {!deleteMode && (
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/users/${user.id}/edit`}>
                            <Pencil className="h-3.5 w-3.5 mr-1.5" />
                            Update
                          </Link>
                        </Button>
                      )}
                      {!updateMode && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-400 border-red-500/40 hover:text-red-300"
                          onClick={() =>
                            setUserToDelete({
                              id: user.id,
                              name: user.name,
                              email: user.email,
                            })
                          }
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-core">
              <div className="text-sm text-chromium-mist">
                Page {page + 1} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={!!userToDelete}
        onOpenChange={(open) => {
          if (!open && !deleteMutation.isPending) {
            setUserToDelete(null);
            deleteMutation.reset();
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              {userToDelete
                ? `${userToDelete.name} (${userToDelete.email}) will be permanently removed. This is blocked when the user owns subscription history.`
                : "This user will be permanently removed."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteMutation.error && (
            <p className="admin-form-error">{deleteMutation.error.message}</p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="border border-red-500/60 bg-transparent text-red-300 hover:bg-red-500/10"
              disabled={!userToDelete || deleteMutation.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (userToDelete) {
                  deleteMutation.mutate({ userId: userToDelete.id });
                }
              }}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete user"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
